// lib/orderCatalog.mjs — R115 C4 (Jesse: "Supplies page → create supply
// request: no options to choose from; should show all the standard
// supplies"). The office "New restock order" modal only had a <datalist> on
// the item box: nothing was listed on screen, and on iOS WebKit a datalist
// suggests nothing until the typed text matches a catalog name ("Pool"
// matched none of the 13, so it went in as a free-text $0 line). The modal now
// LISTS what can be ordered, built here (pure — the modal in
// client/src/pages/SuppliesPage.ts runs this exact code, and
// `node lib/orderCatalog.test.mjs` tests it):
//
//   1. "Catalog" — the priced standard supplies (GET /supplies/property/:lid
//      `effective`, i.e. the standard catalog with this property's variant
//      note / price applied; the plain standard list before a property is
//      picked), grouped by catalog category in catalog order.
//   2. "Inventory checklist" — the inventory template's items (GET
//      /api/ob/amenities-checklist, categories with kind 'inventory') that the
//      catalog does not already carry, grouped by template category. They have
//      no catalog price yet; durable categories (utensils, serveware,
//      cookware, the kitchen drawer) start collapsed.
//
// Lines keep the catalog's EXACT name — the server stamps each order line's
// spec (vendor / link / property variant) by exact lowercased name
// (createSupplyOrder in server/assetops.js). Dedupe uses baseName (equality
// only — "Coffee Filters" is not "Coffee").

/** Inventory categories that hold durable goods (rarely re-ordered) — collapsed by default. */
export const DURABLE_RE = /utensil|serveware|cookware|kitchen drawer/i;

const CUTS = [' w/', ' with ', '(', ' - ', ' — ', ' – '];

/** A draft line's identity (R115 review): the WHOLE name, lowercase, whitespace collapsed, each word's plural "s"
 *  dropped — "toilet paper" = "Toilet Paper", "Paper towel" = "Paper Towels", but "Light bulbs (A19)" ≠ "Light bulbs
 *  (BR30)" and "Coffee (decaf)" ≠ "Coffee" (a qualifier is a different thing to order — base-name merging lost lines). */
export function lineKey(s) {
  return String(s == null ? '' : s).toLowerCase().replace(/\s+/g, ' ').trim()
    .split(' ').map(w => (w.length > 3 && w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w)).join(' ');
}

/** Comparable base name: lowercase, trimmed, cut at a qualifier (" w/ …", "(…", " - …"), whitespace collapsed, a plural "s" dropped. */
export function baseName(s) {
  let t = String(s == null ? '' : s).toLowerCase();
  let cut = -1;   // the EARLIEST qualifier wins ("mop - with pads" → "mop")
  for (const c of CUTS) { const i = t.indexOf(c); if (i > 0 && (cut < 0 || i < cut)) cut = i; }
  if (cut > 0) t = t.slice(0, cut);
  t = t.replace(/\s+/g, ' ').trim();
  if (t.length > 3 && t.endsWith('s') && !t.endsWith('ss')) t = t.slice(0, -1);
  return t;
}

const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v))) ? null : Number(v);

/**
 * Build the picker's groups.
 * @param {{ effective?: any[], inventoryCategories?: any[] }} input
 */
export function buildOrderCatalog({ effective = [], inventoryCategories = [] } = {}) {
  const groups = [];
  const seen = new Set();
  const byCat = new Map();
  const seenCatalog = new Set();   // R115 review: catalog rows dedupe by their own key only — "Trash Bags (13 gal)" and "(33 gal)" both list
  for (const s of (Array.isArray(effective) ? effective : [])) {
    if (!s || !String(s.name || '').trim()) continue;
    const name = String(s.name).trim();
    const k = lineKey(name);
    if (seenCatalog.has(k)) continue;
    seenCatalog.add(k);
    seen.add(baseName(name));   // base names still keep inventory-template duplicates of a catalog item out
    const category = String(s.category || '').trim() || 'General';
    if (!byCat.has(category)) byCat.set(category, []);
    byCat.get(category).push({
      key: `catalog:${name}`,
      name,
      category,
      unit: String(s.unit || '').trim(),
      pack_qty: Number(s.pack_qty) >= 1 ? Math.round(Number(s.pack_qty)) : 1,
      price: num(s.target_price),
      vendor: String(s.vendor || '').trim(),
      note: String(s.note || '').trim(),
      variant: s.source === 'property' && !!String(s.note || '').trim(),
      source: 'catalog',
    });
  }
  for (const [category, items] of byCat) {
    groups.push({ key: `catalog:${category}`, label: category, source: 'catalog', collapsed: false, items });
  }
  for (const c of (Array.isArray(inventoryCategories) ? inventoryCategories : [])) {
    if (!c || c.kind !== 'inventory') continue;
    const category = String(c.category || '').trim() || 'Inventory';
    const items = [];
    for (const raw of (Array.isArray(c.items) ? c.items : [])) {
      const name = String(raw == null ? '' : raw).trim();
      if (!name) continue;
      const b = baseName(name);
      if (!b || seen.has(b)) continue;
      seen.add(b);
      items.push({ key: `inventory:${name}`, name, category, unit: '', pack_qty: 1, price: null, vendor: '', note: '', variant: false, source: 'inventory' });
    }
    if (items.length) groups.push({ key: `inventory:${category}`, label: category, source: 'inventory', collapsed: DURABLE_RE.test(category), items });
  }
  const count = (src) => groups.filter(g => g.source === src).reduce((n, g) => n + g.items.length, 0);
  return { groups, counts: { catalog: count('catalog'), inventory: count('inventory') } };
}

/** Case-insensitive filter over name, category, vendor and variant note; a non-empty query opens every group that still has a match. */
export function filterOrderCatalog(groups, q) {
  const needle = String(q == null ? '' : q).trim().toLowerCase();
  if (!needle) return groups;
  const out = [];
  for (const g of groups) {
    const items = g.items.filter(it => [it.name, it.category, it.vendor, it.note].some(v => String(v || '').toLowerCase().includes(needle)));
    if (items.length) out.push({ ...g, collapsed: false, items });
  }
  return out;
}

/** Index of the draft line that is "the same thing" as name (lineKey equality — never a base name), or -1. */
export function draftIndexOf(draft, name) {
  const k = lineKey(name);
  if (!k) return -1;
  return (draft || []).findIndex(l => lineKey(l.name) === k);
}

/** Packs of this item already in the draft. */
export function qtyInDraft(draft, name) {
  const i = draftIndexOf(draft, name);
  return i < 0 ? 0 : Number(draft[i].qty) || 0;
}

/**
 * Add delta packs of item (a picker row or { name, price, source:'custom' }) to the draft.
 * Same base name merges into one line; reaching 0 removes the line. A
 * catalog item adopts the catalog's exact name (so the server stamps its
 * spec) and fills a missing price. Unpriced lines carry unpriced:true (the
 * draft table says "no price" instead of $0.00). Returns a NEW array.
 */
export function addToDraft(draft, item, delta) {
  const next = (draft || []).map(l => ({ ...l }));
  const d = Math.round(Number(delta) || 0);
  const name = String(item && item.name || '').trim();
  if (!name || !d) return next;
  const price = num(item.price);
  const i = draftIndexOf(next, name);
  if (i < 0) {
    if (d <= 0) return next;
    next.push({ name, qty: d, unit_price: price == null ? 0 : price, source: item.source || 'custom', unpriced: price == null });
    return next;
  }
  const line = next[i];
  line.qty = (Number(line.qty) || 0) + d;
  if (line.qty <= 0) { next.splice(i, 1); return next; }
  if (item.source === 'catalog') {
    line.name = name;
    line.source = 'catalog';
    if ((line.unpriced || !Number(line.unit_price)) && price != null) { line.unit_price = price; line.unpriced = false; }
  }
  return next;
}

/** Set a line's packs directly (0 removes it). Returns a NEW array. */
export function setDraftQty(draft, name, qty) {
  const cur = qtyInDraft(draft, name);
  const i = draftIndexOf(draft, name);
  if (i < 0) return (draft || []).map(l => ({ ...l }));
  return addToDraft(draft, { name: draft[i].name, price: draft[i].unpriced ? null : draft[i].unit_price, source: draft[i].source }, Math.max(0, Math.round(Number(qty) || 0)) - cur);
}

/** Order total of the draft (unpriced lines count $0). */
export function draftTotal(draft) {
  return Math.round((draft || []).reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0) * 100) / 100;
}

/** The POST /supplies/orders items — exactly { name, qty, unit_price } per line (client-only flags dropped). */
export function toOrderItems(draft) {
  return (draft || []).filter(l => l && String(l.name || '').trim() && Number(l.qty) > 0)
    .map(l => ({ name: String(l.name).trim(), qty: Number(l.qty), unit_price: Number(l.unit_price) || 0 }));
}
